import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Activity, Settings, BarChart, FileText, Stethoscope, Calendar, Brain, Heart, Database, Loader2 } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { API_URL } from '../../config/api';
import logger from '../../utils/logger';

const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className={`${bgColor} rounded-2xl p-5 border hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-3xl font-bold text-gray-900 tabular-nums">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/stats`);
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        logger.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const adminTools = [
    {
      title: 'Admin Panel',
      description: 'Comprehensive user management, system stats, and audit logs',
      icon: Shield,
      color: 'bg-purple-500',
      path: '/admin/panel'
    },
    {
      title: 'HealthTrack Pro',
      description: 'Manage health platform features and data',
      icon: Activity,
      color: 'bg-green-500',
      path: '/dashboard/health'
    }
  ];

  const statCards = stats ? [
    { icon: Users, label: 'Total Users', value: stats.users, color: 'bg-blue-500', bgColor: 'bg-blue-50 border-blue-100' },
    { icon: Stethoscope, label: 'Doctors', value: stats.doctors, color: 'bg-emerald-500', bgColor: 'bg-emerald-50 border-emerald-100' },
    { icon: Heart, label: 'Patients', value: stats.patients, color: 'bg-rose-500', bgColor: 'bg-rose-50 border-rose-100' },
    { icon: Calendar, label: 'Appointments', value: stats.appointments, color: 'bg-amber-500', bgColor: 'bg-amber-50 border-amber-100' },
    { icon: FileText, label: 'Lab Reports', value: stats.lab_reports, color: 'bg-indigo-500', bgColor: 'bg-indigo-50 border-indigo-100' },
    { icon: Brain, label: 'AI Analyses', value: stats.ai_analyses, color: 'bg-purple-500', bgColor: 'bg-purple-50 border-purple-100' },
    { icon: Activity, label: 'Prescriptions', value: stats.prescriptions, color: 'bg-teal-500', bgColor: 'bg-teal-50 border-teal-100' },
    { icon: Database, label: 'Organizations', value: stats.organizations, color: 'bg-orange-500', bgColor: 'bg-orange-50 border-orange-100' },
  ] : [];

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="max-w-7xl mx-auto" data-testid="admin-dashboard">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Admin Control Center</h2>
          <p className="text-gray-600 mt-2">Manage users, monitor system health, and access all platform features</p>
        </div>

        {/* Live Platform Stats */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Platform Overview (Live Data)</h3>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading platform stats...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statCards.map((s, i) => (
                <StatCard key={`stat-${s.title || i}`} {...s} />
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminTools.map((tool, index) => (
            <div
              key={`admin-tool-${tool.title || index}`}
              onClick={() => navigate(tool.path)}
              className="bg-white rounded-2xl shadow-premium border border-gray-100 p-6 hover:shadow-premium-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              data-testid={`admin-tool-${tool.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`${tool.color} w-12 h-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}>
                <tool.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.title}</h3>
              <p className="text-gray-600 text-sm">{tool.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-8 border border-indigo-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/admin/panel')}
              className="bg-white px-6 py-4 rounded-lg shadow-sm hover:shadow-md transition text-left"
              data-testid="quick-action-manage-users"
            >
              <div className="flex items-center">
                <Users className="w-5 h-5 text-indigo-600 mr-3" />
                <span className="font-medium text-gray-900">Manage Users</span>
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/panel')}
              className="bg-white px-6 py-4 rounded-lg shadow-sm hover:shadow-md transition text-left"
              data-testid="quick-action-system-stats"
            >
              <div className="flex items-center">
                <BarChart className="w-5 h-5 text-purple-600 mr-3" />
                <span className="font-medium text-gray-900">View System Stats</span>
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/panel')}
              className="bg-white px-6 py-4 rounded-lg shadow-sm hover:shadow-md transition text-left"
              data-testid="quick-action-audit-logs"
            >
              <div className="flex items-center">
                <Activity className="w-5 h-5 text-green-600 mr-3" />
                <span className="font-medium text-gray-900">Audit Logs</span>
              </div>
            </button>
            <button
              onClick={() => navigate('/pitch-deck')}
              className="bg-white px-6 py-4 rounded-lg shadow-sm hover:shadow-md transition text-left"
              data-testid="quick-action-pitch-deck"
            >
              <div className="flex items-center">
                <Settings className="w-5 h-5 text-orange-600 mr-3" />
                <span className="font-medium text-gray-900">View Pitch Deck</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
